import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControlLabel,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import { addWardApi, updateWardApi } from "../../../services/ward.service";
import WardMasterSchema from "../../../joi/master/ward-master.schema";
import { useGetApi } from "../../../hooks/useGetApi";
import { getUserApi } from "../../../services/user.service";

export default function WardMasterModal({
  open,
  onClose,
  refetch,
  selectedRow = null,
}) {
  const initialState = {
    wardName: "",
    extensionNumber: "",
    contactNumber: "",
    matronInCharge: null,
    hasBlock: false,
    active: true,
    withinWardDirectTransfer: false,
    isOTWard: false,
    isICUWard: false,
    priority: 0,
  };
  const [formData, setFormData] = useState(initialState);
  const [isLoading, setIsLoading] = useState(false);

  const { dataList: userList } = useGetApi({
    apiFunction: getUserApi,
    params: { limit: Number.MAX_SAFE_INTEGER },
    dataKey: "users",
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevValue) => {
      let next = value;
      if (type !== "checkbox" && name === "contactNumber") {
        next = value.replace(/\D/g, "").slice(0, 10);
      }
      return {
        ...prevValue,
        [name]:
          type === "checkbox"
            ? checked
            : name === "priority"
            ? Number(value)
            : next,
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let response;

    const { error } = WardMasterSchema.validate({
      ...formData,
      bedSubCategory: formData?.bedSubCategory?._id,
    });

    if (error) {
      error.details.forEach((err) => toast.error(err.message));
      return;
    }
    console.log(formData);

    setIsLoading(true);
    if (selectedRow?._id) {
      response = await updateWardApi({
        ...formData,
        matronInCharge: formData?.matronInCharge?._id,
      });
    } else {
      response = await addWardApi({
        ...formData,
        matronInCharge: formData?.matronInCharge?._id,
      });
    }
    setIsLoading(false);

    if (response?.code === 200) {
      toast.success(
        response?.message ||
          `Ward ${selectedRow?._id ? "updated" : "added"} successfully`
      );
      setFormData(initialState);
      onClose();
      refetch();
    } else {
      toast.error(response?.message || "Some error occurred.");
    }
  };

  useEffect(() => {
    if (selectedRow?._id) {
      setFormData({
        ...initialState,
        _id: selectedRow._id,
        wardName: selectedRow?.wardName || "",
        extensionNumber: selectedRow?.extensionNumber || "",
        contactNumber: String(selectedRow?.contactNumber ?? "")
          .replace(/\D/g, "")
          .slice(0, 10),
        matronInCharge: selectedRow?.matronInCharge || "",
        hasBlock: selectedRow?.hasBlock ?? false,
        active: selectedRow?.active ?? true,
        withinWardDirectTransfer:
          selectedRow?.withinWardDirectTransfer ?? false,
        isOTWard: selectedRow?.isOTWard ?? false,
        isICUWard: selectedRow?.isICUWard ?? false,
        priority: selectedRow?.priority ?? 0,
      });
    } else {
      setFormData(initialState);
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            maxWidth: "unset",
            width: { xs: "95vw", lg: "1100px" },
            bgcolor: "#E5F4FF",
            borderRadius: "20px",
          },
        },
      }}
    >
      <DialogContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: "20px",
          }}
        >
          <Typography sx={{ color: "rgba(93, 106, 130, 1)", fontSize: 20 }}>
            {selectedRow?._id ? `Edit Ward` : `Add Ward`}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Ward Name"
                name="wardName"
                value={formData?.wardName || ""}
                onChange={handleChange}
                required
                fullWidth
                sx={{ bgcolor: "white" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Extension Number"
                name="extensionNumber"
                value={formData?.extensionNumber || ""}
                onChange={handleChange}
                fullWidth
                sx={{ bgcolor: "white" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Contact Number"
                name="contactNumber"
                value={formData?.contactNumber || ""}
                onChange={handleChange}
                fullWidth
                placeholder="10 digits (optional)"
                inputProps={{
                  maxLength: 10,
                  inputMode: "numeric",
                  autoComplete: "tel",
                }}
                sx={{ bgcolor: "white" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={8}>
              {/* <TextField
                label="Bed Sub Category"
                name="bedSubCategory"
                value={formData?.bedSubCategory || ""}
                onChange={handleChange}
                fullWidth
                required
                sx={{ bgcolor: "white" }}
              /> */}

              <Autocomplete
                options={userList || []}
                getOptionLabel={(option) =>
                  option?.firstName && option?.lastName
                    ? `${option.firstName} ${option.lastName}`
                    : ""
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Matron In Charge"
                    name="matronInCharge"
                    fullWidth
                  />
                )}
                value={formData?.matronInCharge || null}
                onChange={(_, newValue) =>
                  handleChange({
                    target: { name: "matronInCharge", value: newValue },
                  })
                }
                sx={{ bgcolor: "white" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Priority"
                name="priority"
                value={formData?.priority || ""}
                onChange={handleChange}
                type="number"
                fullWidth
                required
                sx={{ bgcolor: "white" }}
              />
            </Grid>
            {/* <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Matron In Charge"
                name="matronInCharge"
                value={formData?.matronInCharge || ""}
                onChange={handleChange}
                fullWidth
                required
                sx={{ bgcolor: "white" }}
              />
            </Grid> */}
            <Grid item xs={12} md={8}>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="hasBlock"
                        checked={formData?.hasBlock}
                        onChange={handleChange}
                      />
                    }
                    label="Has Block"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="active"
                        checked={formData?.active}
                        onChange={handleChange}
                      />
                    }
                    label="Active"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="withinWardDirectTransfer"
                        checked={formData?.withinWardDirectTransfer}
                        onChange={handleChange}
                      />
                    }
                    label="Within Ward Direct Transfer"
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="isOTWard"
                    checked={formData?.isOTWard}
                    onChange={handleChange}
                  />
                }
                label="Is OT Ward?"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="isICUWard"
                    checked={formData?.isICUWard}
                    onChange={handleChange}
                  />
                }
                label="Is ICU Ward?"
              />
            </Grid>
          </Grid>
          <Box sx={{ display: "flex", justifyContent: "center", mt: "20px" }}>
            <Button
              variant="contained"
              type="submit"
              sx={{
                textTransform: "none",
                backgroundColor: "rgba(1, 121, 176, 1)",
                width: "150px",
              }}
              disabled={isLoading}
            >
              {isLoading ? (
                <CircularProgress size={24} />
              ) : selectedRow?._id ? (
                "Update"
              ) : (
                `Submit`
              )}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
